<i18n>
{
  "en": {
    "dictionaryAttributes": {
      "id": "ID",
      "valueType": "Type",
      "code": "Code",
      "name": "Name",
      "versionedFlag": "Versioned",
      "create": {
        "hint": "Add new dictionary attribute"
      }
    }
  },
  "ru": {
    "dictionaryAttributes": {
      "id": "ID",
      "valueType": "Тип",
      "code": "Код",
      "name": "Наименование",
      "versionedFlag": "Версии",
      "create": {
        "hint": "Новый атрибут справочника"
      }
    }
  }
}
</i18n>

<template>
  <div class="dictionary-attributes">
    <div class="app-buttons">
      <dx-button
        id="create"
        icon="plus"
        type="normal"
        :text="$t('general.create')"
        :hint="$t('dictionaryAttributes.create.hint')"
        @click="onCreate"
      />
    </div>

    <dx-data-grid
      class="data-grid"
      :data-source="attributes"
      :height="gridHeight"
      :show-borders="true"
      @row-inserting="onInsert"
      @row-updating="onSave"
      @row-removing="onRemove"
      @context-menu-preparing="addMenuItems"
      @editor-preparing="onEditorPreparing"
      @content-ready="onContentReady"
      @initialized=initializedGrid
      ref="dataGrid"
    >
      <dx-paging :enabled="true"
        :page-size="rowCountDictionaryAttributes" :page-index="pageIndex"
      />
      <dx-pager
        :visible="true"
        :allowed-page-sizes="pageSize"
        display-mode="full"
        :show-page-size-selector="true"
        :show-info="true"
        :show-navigation-buttons="false"
      />

      <dx-filter-row :visible="true" />
      <dx-header-filter :visible="true" />
      <dx-editing mode="row" :allow-updating="true" :allow-deleting="true" />
      <dx-sorting mode="multiple" />
      <dx-state-storing
        :enabled="true"
        type="custom"
        :customLoad="stateLoad"
        :customSave="stateSave"
      />

      <dx-column
        data-field="valueType"
        :width="120"
        :header-filter="{ dataSource: dictionaryAttributeTypesDataSourceConfig }"
        :caption="$t('dictionaryAttributes.valueType')"
      >
        <dx-lookup
          :data-source="dictionaryAttributeTypesDataSourceConfig"
          value-expr="id"
          display-expr="name"
        />
      </dx-column>

      <dx-column
        :width="120"
        data-field="code"
        :caption="$t('dictionaryAttributes.code')"
        :allow-header-filtering="false"
        :allow-filtering="true"
        :editor-options="{maxLength: 50}"
      />

      <dx-column
        data-field="name"
        :caption="$t('dictionaryAttributes.name')"
        :allow-header-filtering="false"
        :allow-filtering="true"
        :editor-options="{maxLength: 250}"
      />

      <dx-column
        data-field="versionedFlag"
        data-type="boolean"
        :width="120"
        :caption="$t('dictionaryAttributes.versionedFlag')"
      />
    </dx-data-grid>

    <CustomColumnChooser
      :visible="visible"
      :columns="columns"
      :onApply="onApply"
      :onHiding="onHiding"
    />
  </div>
</template>

<script>
import baseView from '../base.vue';

import DxDataGrid, {
  DxColumn, DxFilterRow, DxPaging, DxHeaderFilter, DxLookup, DxEditing, DxStateStoring,
  DxPager, DxSorting
} from "devextreme-vue/data-grid";
import { DxButton } from 'devextreme-vue';

import { dictionaryAttributeTypesDataSourceConfig } from './constants';
import CustomColumnChooser from "@/components/column-chooser/CustomColumnChooser.vue";

import {
  getDictionaryAttributes, putDictionaryAttribute, postDictionaryAttribute,
  deleteDictionaryAttribute
} from '@/services/api';

import { DataDrid } from "@/mixins/dataDridMixin";

import {
  PAGE_HEADER_HEIGHT,
  PAGE_PADDING,
  PAGE_TOOLBARS_HEIGHT,
  PAGE_FOOTER_HEIGHT,
  GRID_HEADER_HEIGHT,
  GRID_FILTER_ROW_HEIGHT,
  GRID_FILTER_PANEL_HEIGHT,
  GRID_PAGER_HEIGHT
} from '@/shared/consts/layout';

export default {
  name: 'dictionary-attributes',
  props: ['dictionary'],
  extends: baseView,
  mixins: [DataDrid],
  components: {
    DxDataGrid, DxColumn, DxFilterRow, DxPaging, DxButton, DxHeaderFilter, DxLookup,
    DxEditing, DxStateStoring, CustomColumnChooser, DxPager, DxSorting
  },
  data() {
    return {
      attributes: null,
      pageIndex: 0,
      dictionaryAttributeTypesDataSourceConfig
    };
  },
  computed: {
    sumGap() {
      return PAGE_HEADER_HEIGHT + PAGE_PADDING + PAGE_TOOLBARS_HEIGHT + PAGE_FOOTER_HEIGHT;
    },
    gridHeight() {
      const appHeightSize = this.$store.getters.appContainerSize.height;
      const tabListHeight = 37;
      const padding = 40;

      return (appHeightSize || 800) -
        this.sumGap -
        tabListHeight -
        padding;
    },
    gridBodyHeight() {
      return this.gridHeight -
        GRID_HEADER_HEIGHT -
        GRID_FILTER_PANEL_HEIGHT -
        GRID_FILTER_ROW_HEIGHT -
        GRID_PAGER_HEIGHT;
    },
    rowCountDictionaryAttributes() {
      return this.countRows(this.gridBodyHeight);
    },
  },
  methods: {
    onEditorPreparing(e) {
      this.applyFilterRowTimeout(e);
    },
    onContentReady(e) {
      this.addFitToPageHeightButton(e);
    },
    async loadData(dictionary) {
      try {
        const data = await getDictionaryAttributes(dictionary.id);
        this.attributes = data;
      } catch(err) {
        // eslint-disable-next-line
        console.error(err);
      }
    },
    onCreate() {
      this.$refs.dataGrid.instance.addRow();
    },
    onInsert(e) {
      e.cancel = new Promise(async (resolve, reject) => {
        try {
          e.data = await postDictionaryAttribute(this.dictionary.id, {
            ...e.data, versionedFlag: e.data.versionedFlag||false
          });
          resolve(false);
        } catch(err) {
          reject(err);
        }
      });
    },
    async onSave(e) {
      e.cancel = new Promise(async (resolve, reject) => {
        try {
          e.data = await putDictionaryAttribute(this.dictionary.id, {
            ...e.oldData, ...e.newData
          });
          resolve(false);
        } catch(err) {
          reject(err);
        }
      });
    },
    async onRemove(e) {
      e.cancel = new Promise(async (resolve, reject) => {
        try {
          await deleteDictionaryAttribute(this.dictionary.id, e.data.id);
          resolve(false);
        } catch(err) {
          reject(err);
        }
      });
    },
  },
  created() {
    this.gridStateKey = 'gridDictionaryAttributes' + this.dictionary.code;

    this.loadData(this.dictionary);
  },
  beforeDestroy() {
    const state = this.dataGrid.state();
    this.stateSave(state);
  },
  watch: {
    dictionary(dictionary) {
      this.loadData(dictionary);
    }
  },
};
</script>

<style lang="scss">
.dictionary-attributes {
  .button-cell {
    text-align: center;
    .dx-button {
      margin: 0 8px;
    }
  }
}
</style>
